import React from 'react';

import { Box, Button, Divider, Tab, Tabs, Typography } from '@mui/material';
import { format } from 'date-fns';
import isSameHour from 'date-fns/isSameHour';
import { useNavigate } from 'react-router-dom';
import { SaveSheet, SheetStatus } from 'src/common/types/SaveSheet';

import {
  useAddLogMutation,
  useGetMeMutation,
} from '../../../common/apis/CommonApi';
import {
  useSaveMultipleNotesMutation,
  useViewedWelcomeNoteMutation,
} from '../../../common/apis/FlorenceApi';
import { ReactComponent as UploadIcon } from '../../../common/assets/icons/upload_icon.svg';
import { FlorencePAGES } from '../../../common/components/tfa-auth/constants/pages';
import { useAppDispatch } from '../../../common/hooks/reduxHooks';
import { useAppSelector } from '../../../common/hooks/reduxHooks';
import {
  clearAllFilters,
  clearProcessedOcrs,
} from '../../../common/stores/modelSlice';
import SnackbarUtils from '../../../common/utils/SnackbarUtilsConfigurator';
import CodesSearchBar from '../../components/CodesSearchBar/CodesSearchBar';
import FeedbackButton from '../../components/FeedbackButton';
import TrialEndedNotePopup from '../../components/NotePopups/TrialEndedNotePopup';
import WelcomeNotePopup from '../../components/NotePopups/WelcomeNotePopup';
import { SearchResultsParams } from '../../types/RouterState';

import './HomeScreen.scss';
import { Container } from '@mui/system';
import { TabContext, TabPanel } from '@mui/lab';

import MyFavoriteCodes from './components/MyFavoriteCodes';
import MyNotes from './components/MyNotes';
import UploadDocuments from './components/UploadDocuments';

type SelectedTab = 'OpNotes' | 'ClinicNotes' | 'FavoriteCodes';

const HomeScreen: React.FC = () => {
  const { user } = useAppSelector((state) => state.model);

  const [selectedTab, setSelectedTab] = React.useState<SelectedTab>(
    (localStorage.getItem('lastSelectedTab') as SelectedTab) ?? 'ClinicNotes'
  );

  const [uploadDocumentsOpen, setUploadDocumentsOpen] = React.useState(false);

  const changeTab = (event: React.SyntheticEvent, newValue: SelectedTab) => {
    localStorage.setItem('lastSelectedTab', newValue);
    setSelectedTab(newValue);
  };

  const [viewedWelcomeNote] = useViewedWelcomeNoteMutation();
  const [getMeMutation] = useGetMeMutation();
  const [addLogTrigger] = useAddLogMutation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [saveMupltipleNotes, { isLoading }] = useSaveMultipleNotesMutation();

  const handleSearch = async (text: string) => {
    if (text === '') return;

    if (text.length >= 250 && !user?.organizationId) {
      if (!user?.activeSubscriptionType) {
        navigate(FlorencePAGES.subscriptions);
        SnackbarUtils.error('No active subscription');
        return;
      } else if (user.activeSubscriptionType === 'trial') {
        if (
          user.trialScansAvailable === 0 &&
          user.purchasedScansAvailable === 0
        ) {
          navigate(FlorencePAGES.scanPackage);
          SnackbarUtils.error('Out of scans');
          return;
        }
      } else {
        if (user.purchasedScansAvailable === 0) {
          navigate(FlorencePAGES.scanPackage);
          SnackbarUtils.error('Out of scans');
          return;
        }
      }
    }

    if (selectedTab === 'ClinicNotes') {
      const response = await saveNote(text, true);
      const note: SaveSheet | null = response?.length ? response[0] : null;
      if (!note) {
        SnackbarUtils.error(
          'There has been an error. Please refresh the page and try again.'
        );
      }

      navigate(FlorencePAGES.results, {
        state: {
          searchText: '',
          searchType: 'scan',
          notes: [note],
        } as SearchResultsParams,
      });
    } else {
      if (text.length >= 250) {
        // Create an OP Note
        const response = await saveNote(text, false);
        const note: SaveSheet | null = response?.length ? response[0] : null;
        if (!note) {
          SnackbarUtils.error(
            'There has been an error. Please refresh the page and try again.'
          );
        }

        navigate(FlorencePAGES.results, {
          state: {
            searchText: '',
            searchType: 'scan',
            notes: [note],
          } as SearchResultsParams,
        });
      } else {
        navigate(FlorencePAGES.results, {
          state: {
            searchText: text,
            searchType: 'text',
            notes: [],
          } as SearchResultsParams,
        });
      }
    }
  };

  const saveNote = async (text: string, clinicNote: boolean) => {
    try {
      return await saveMupltipleNotes([
        {
          scanText: text,
          scanDocumentUrl: '',
          sheetCodes: [],
          name: '',
          documentName: '',
          status: SheetStatus.UPLOADED,
          clinicNote: clinicNote,
        },
      ]).unwrap();
    } catch {
      SnackbarUtils.error('There has been an error. Please try again later.');
      return null;
    }
  };

  const displayDocuSignPopup = () => {
    if (!user) return;
    // If the user did not agree with the BAA then display the popup.
    // The ClickWrap JS script was loaded externally in index.html.
    // The localStorage check is to prevent unnecessary requests.
    const dateLastBAACheck = localStorage.getItem('dateLastBAACheck');
    if (dateLastBAACheck) {
      const now = Date.now();
      const sameHour = isSameHour(now, parseInt(dateLastBAACheck));
      if (sameHour) return;
    }

    const date = new Date();
    const dayNumber = format(date, 'do');
    const monthName = format(date, 'LLLL');
    const year = format(date, 'yyyy');
    const formattedDate = `${dayNumber} day of ${monthName}, ${year}`;
    // eslint-disable-next-line
    (window as any).docuSignClick.Clickwrap.render(
      {
        environment: 'https://na4.docusign.net',
        accountId: '7d01337c-13ee-40fa-a316-db535eb10b2c',
        clickwrapId: '1b76dfb1-c4f9-4672-a5be-28a2ebb2c6d3',
        clientUserId: user.email,
        documentData: {
          date: formattedDate,
          fullName: user.fullName ?? '',
          email: user.email ?? '',
          title: user.practiceName ?? '',
          company: user.state ?? '',
        },
        onAgreed: function () {
          localStorage.setItem('dateLastBAACheck', Date.now().toString());
        },
      },
      '#docusign-clickwrap-business-associate-agreement'
    );
  };

  const sendLastActivityEvent = async () => {
    if (!user) return;

    const dateLastOpenedApp = localStorage.getItem('dateLastOpenedApp');
    if (dateLastOpenedApp) {
      const now = Date.now();
      const sameHour = isSameHour(now, parseInt(dateLastOpenedApp));
      if (sameHour) return;
    }

    await addLogTrigger('Opened app (web)');
    localStorage.setItem('dateLastOpenedApp', Date.now().toString());
  };

  // We want to reset this if they ever go back or log back in after setting filters
  React.useLayoutEffect(() => {
    dispatch(clearAllFilters());
    dispatch(clearProcessedOcrs());
    displayDocuSignPopup();
    sendLastActivityEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderNotesContainer = (clinicNotes: boolean) => (
    <>
      <Typography
        color={
          clinicNotes ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 33, 69, 0.85)'
        }
        fontSize='32px'
        fontWeight={700}
      >
        {clinicNotes ? 'Clinic' : 'Op'} Notes
      </Typography>
      <Box
        className='search-container'
        style={{
          background: clinicNotes ? '#0CBDD4' : '#0D4E5C',
        }}
      >
        <Typography color='white' fontSize='18px' fontWeight={700}>
          Search {clinicNotes ? 'Clinic' : 'Op'} Notes for Codes
        </Typography>
        <Typography color='white' fontSize='18px' fontWeight={400}>
          Find CPT codes by entering a description or by uploading files to scan
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '20px',
            rowGap: '8px',
            columnGap: '16px',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          <CodesSearchBar
            handleSearch={handleSearch}
            isLoading={isLoading}
            clinicNotes={clinicNotes}
          />
          <Typography
            fontSize='16px'
            color='white'
            fontWeight={500}
            textAlign='center'
            sx={{
              width: { xs: '100%', md: 'auto' },
            }}
          >
            or
          </Typography>
          <Button
            onClick={() => setUploadDocumentsOpen(true)}
            disabled={isLoading}
            sx={{
              borderRadius: '100px',
              height: '50px',
              minWidth: { xs: '100%', md: '208px' },
              backgroundColor: clinicNotes ? '#0D4E5C' : '#0CBDD4',
              color: '#fff',
            }}
            color='primary'
            variant='contained'
            startIcon={<UploadIcon />}
            disableElevation
          >
            <Typography noWrap fontWeight={500}>
              Upload documents
            </Typography>
          </Button>
        </Box>
      </Box>
      <Divider
        sx={{
          borderColor: 'rgba(82, 86, 98, 0.2)',
          marginTop: '80px',
          marginBottom: '80px',
        }}
      />
      <MyNotes clinicNotes={clinicNotes} />
    </>
  );

  return (
    <>
      <div id='docusign-clickwrap-business-associate-agreement' />
      <Box className='home-screen-wrapper'>
        {user &&
          user.activeSubscriptionType === 'trial' &&
          !user.isViewedWelcomeNote && (
            <WelcomeNotePopup
              onClose={async () => {
                await viewedWelcomeNote();
                await getMeMutation();
              }}
            />
          )}
        {user &&
          !user.activeSubscriptionType &&
          user.isViewedWelcomeNote &&
          !user.organizationId && <TrialEndedNotePopup />}
        <Container maxWidth='xl'>
          <TabContext value={selectedTab}>
            <Tabs
              className='tabs-container'
              value={selectedTab}
              onChange={changeTab}
              TabIndicatorProps={{ style: { display: 'none' } }}
            >
              <Tab
                value='ClinicNotes'
                label='Clinic Notes'
                style={{
                  background:
                    selectedTab === 'ClinicNotes'
                      ? '#0D4E5C'
                      : 'rgba(13, 78, 92, 0.10)',
                  color:
                    selectedTab === 'ClinicNotes'
                      ? 'rgba(255, 255, 255, 0.85)'
                      : 'rgba(13, 78, 92, 0.50)',
                }}
              />
              <Tab
                value='OpNotes'
                label='Op Notes'
                style={{
                  background:
                    selectedTab === 'OpNotes'
                      ? 'rgba(12, 189, 212, 0.16)'
                      : 'rgba(13, 78, 92, 0.10)',
                  color:
                    selectedTab === 'OpNotes'
                      ? 'rgba(0, 33, 69, 0.85)'
                      : 'rgba(13, 78, 92, 0.50)',
                }}
              />
              <Tab
                value='FavoriteCodes'
                label='Favorite Codes'
                style={{
                  background:
                    selectedTab === 'FavoriteCodes'
                      ? '#D1DEDE'
                      : 'rgba(13, 78, 92, 0.10)',
                  color:
                    selectedTab === 'FavoriteCodes'
                      ? 'rgba(0, 33, 69, 0.85)'
                      : 'rgba(13, 78, 92, 0.50)',
                }}
              />
            </Tabs>
            <Box
              className='panel-container'
              sx={{
                background:
                  selectedTab === 'ClinicNotes'
                    ? '#0D4E5C'
                    : selectedTab === 'OpNotes'
                    ? 'rgba(12, 189, 212, 0.16)'
                    : '#D1DEDE',
              }}
            >
              <TabPanel value='ClinicNotes' sx={{ width: '100%' }}>
                {uploadDocumentsOpen && (
                  <UploadDocuments
                    onClose={() => setUploadDocumentsOpen(false)}
                    clinicNotes={true}
                  />
                )}
                {!uploadDocumentsOpen && renderNotesContainer(true)}
              </TabPanel>
              <TabPanel value='OpNotes' sx={{ width: '100%' }}>
                {uploadDocumentsOpen && (
                  <UploadDocuments
                    onClose={() => setUploadDocumentsOpen(false)}
                    clinicNotes={false}
                  />
                )}
                {!uploadDocumentsOpen && renderNotesContainer(false)}
              </TabPanel>

              <TabPanel value='FavoriteCodes' sx={{ width: '100%' }}>
                <Typography
                  color='text.primary'
                  fontSize='32px'
                  fontWeight={700}
                >
                  Favorite Codes
                </Typography>
                <Typography
                  color='text.primary'
                  fontSize='18px'
                  fontWeight={400}
                  marginBottom='32px'
                >
                  View the codes you have saved as favorites
                </Typography>
                <MyFavoriteCodes />
              </TabPanel>
            </Box>
          </TabContext>
        </Container>
        <FeedbackButton />
        <Typography
          sx={{ alignSelf: 'center', marginTop: 'auto' }}
          py='16px'
          fontSize='14px'
          fontWeight={500}
          color='secondary'
        >
          v{process.env.REACT_APP_VERSION}
        </Typography>
      </Box>
    </>
  );
};

export default HomeScreen;
